import React, { useState } from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import type { LinkProps } from 'src/components/ui/Link';
import Link from 'src/components/ui/Link';
import PropertyCard from 'src/components/ad/PropertyCard';
import HorizontalScroller, { SnapItem } from 'src/components/ui/HorizontalScroller';
import Typography from 'src/components/ui/Typography';
import { Tabs, Tab } from 'src/components/ui/Tabs';
import MediaCarousel from 'src/components/ui/MediaCarousel';
import { GalleryPreviewCard } from 'src/components/favorites/GalleryPreview';
import PostCard from 'src/components/news/posts/PostCard';

import { cardHeightInRemForGalleries, cardHeightInRemForAds, cardHeightInRemForPosts } from 'src/constants/misc';
import { colours } from 'src/constants/colours';
import theme from 'src/utils/theme';

import { useUserAdsQuery, usePostsQuery } from 'src/hooks/__generated__/queries';
import type { Ad, UserProfileQuery, Medium, Post } from 'src/types/__generated__/graphql';
import { AdStatuses, GalleryAccessPolicy } from 'src/types/__generated__/graphql';
import { routePathUser } from 'src/constants/router';
import { styledComponent, styledTag } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { useGalleries } from 'src/hooks/useGalleries';

const StyledCardContent = styledComponent(CardContent)`
    position: relative;
    padding: 1.5rem 0;
`;

const MediaCarouselWrapper = styledTag('div')`
    height: 14rem;
    cursor: pointer;
`;

const StyledLink = styledComponent((props: LinkProps) => <Link {...props} />)`
    color: ${theme.palette.primary.main};
    font-size: 0.875rem;
    font-weight: bold;
`;

export type TabTypes = 'publications' | 'galleries' | 'ads';

export interface Props {
    userProfile: NonNullable<UserProfileQuery['userProfile']>;
}

const defaultLimit = 5;

const analyticsMapping: Record<TabTypes, AnalyticsEvent> = {
    publications: AnalyticsEvent.ActivityPublications,
    galleries: AnalyticsEvent.ActivityGalleries,
    ads: AnalyticsEvent.ActivityAds,
};

const ActivitySection: React.FC<Props> = ({ userProfile }) => {
    const { t } = useTranslation(['profile']);
    const { logEventDynamic } = useDynamicLogEvents();

    const { isMobile } = useBreakpoints();

    const [selectedTab, setSelectedTab] = useState<TabTypes>(userProfile.isProfessionnal ? 'publications' : 'ads');

    const {
        previousData: userAdsPreviousData,
        data: userAds = userAdsPreviousData,
        fetchMore: fetchMoreUserAds,
        loading: loadingUserAds,
    } = useUserAdsQuery({
        variables: {
            userId: String(userProfile.id),
            offset: 0,
            limit: defaultLimit,
            statuses: [AdStatuses.Available, AdStatuses.Ended],
        },
        fetchPolicy: 'cache-and-network',
        skip: userProfile.isProfessionnal || selectedTab !== 'ads',
        notifyOnNetworkStatusChange: true,
    });

    const {
        previousData: userPostsPreviousData,
        data: userPosts = userPostsPreviousData,
        fetchMore: fetchMoreUserPosts,
        loading: loadingUserPosts,
    } = usePostsQuery({
        variables: {
            skip: 0,
            take: defaultLimit,
            userId: String(userProfile.id),
        },
        skip: !userProfile.isProfessionnal || selectedTab !== 'publications',
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const {
        items: galleries,
        totalCount,
        loading,
        fetchMore,
    } = useGalleries({
        userId: userProfile.id,
        policy: GalleryAccessPolicy.Public,
        limit: defaultLimit,
        skip: selectedTab !== 'galleries',
        fetchPolicy: 'cache-and-network',
    });

    const handleTabChange = (_ev: React.SyntheticEvent, tab: TabTypes) => {
        logEventDynamic(analyticsMapping[tab]);
        setSelectedTab(tab);
    };

    const ads = userAds?.userAds ?? [];
    const posts = userPosts?.posts ?? [];

    const cardsWidthInRem = {
        ads: 18,
        publications: 20,
        galleries: 18,
    };

    let horizontalScrollerProps;
    switch (selectedTab) {
        case 'ads':
            horizontalScrollerProps = {
                children: ads.map((ad) => (
                    <SnapItem key={ad.id}>
                        <Box width={`${cardsWidthInRem.ads}rem`}>
                            <PropertyCard ad={ad as Ad} isDarkCard />
                        </Box>
                    </SnapItem>
                )),
                actualCardsLength: ads.length,
                cardHeightInRem: cardHeightInRemForAds,
                totalCardsLength: userProfile.numberOfAds,
                loading: loadingUserAds,
                cardWidthInPx: cardsWidthInRem.ads * 16, // 18rem * 16
                handleFetchMore: () =>
                    fetchMoreUserAds({
                        variables: {
                            offset: ads.length,
                        },
                    }),
            };
            break;
        case 'publications':
            horizontalScrollerProps = {
                children: posts.map((post) => {
                    const images = () => (
                        <MediaCarouselWrapper>
                            <MediaCarousel media={post.media as Medium[]} alt={String(post.message)} />
                        </MediaCarouselWrapper>
                    );
                    return (
                        <SnapItem key={post.id}>
                            <Box width={`${cardsWidthInRem.publications}rem`}>
                                <PostCard
                                    carousel={images}
                                    post={post as Post}
                                    showHeader={false}
                                    backgroundColor={colours.black.O04}
                                />
                            </Box>
                        </SnapItem>
                    );
                }),
                actualCardsLength: posts.length,
                totalCardsLength: userProfile.numberOfPosts,
                loading: loadingUserPosts,
                cardHeightInRem: cardHeightInRemForPosts,
                cardWidthInPx: cardsWidthInRem.publications * 16, // 25rem * 16
                marginBottom: 2,
                handleFetchMore: () =>
                    fetchMoreUserPosts({
                        variables: {
                            skip: posts.length,
                        },
                    }),
            };
            break;
        case 'galleries':
            horizontalScrollerProps = {
                children: galleries?.map((gallery) => (
                    <SnapItem key={gallery.id}>
                        <Box width={`${cardsWidthInRem.galleries}rem`} height="12.25rem">
                            <GalleryPreviewCard gallery={gallery} />
                        </Box>
                    </SnapItem>
                )),
                actualCardsLength: galleries?.length,
                totalCardsLength: totalCount,
                loading,
                cardHeightInRem: cardHeightInRemForGalleries,
                cardWidthInPx: cardsWidthInRem.galleries * 16, // 18rem * 16
                handleFetchMore: fetchMore,
            };
            break;
        default:
            break;
    }

    return (
        <Card elevation={0}>
            <StyledCardContent>
                <Typography variant="h2" serif>
                    {t('profile:activity')}
                </Typography>
                <Box display="flex" alignItems="center" my={2}>
                    <Tabs value={selectedTab} onChange={handleTabChange}>
                        {userProfile.isProfessionnal ? (
                            <Tab
                                label={
                                    isMobile
                                        ? t('profile:publications')
                                        : `${t('profile:publications')} (${userProfile.numberOfPosts})`
                                }
                                value="publications"
                            />
                        ) : (
                            <Tab
                                label={isMobile ? t('profile:ads') : `${t('profile:ads')} (${userProfile.numberOfAds})`}
                                value="ads"
                            />
                        )}
                        <Tab
                            label={
                                isMobile
                                    ? t('profile:galleries')
                                    : `${t('profile:galleries')} (${userProfile.numberOfGalleries})`
                            }
                            value="galleries"
                        />
                    </Tabs>
                </Box>
                <HorizontalScroller key={`${selectedTab}-HorizontalScroller`} {...horizontalScrollerProps} />
                <Box textAlign={isMobile ? 'center' : 'right'}>
                    {selectedTab === 'publications' && posts.length > 0 ? (
                        <StyledLink href={`${routePathUser}/${userProfile.id}/posts`}>
                            {t('profile:seeAllPublications')}
                        </StyledLink>
                    ) : null}
                    {selectedTab === 'galleries' && (galleries?.length ?? 0) > 0 ? (
                        <StyledLink href={`${routePathUser}/${userProfile.id}/galleries`}>
                            {t('profile:seeAllGalleries')}
                        </StyledLink>
                    ) : null}
                </Box>
            </StyledCardContent>
        </Card>
    );
};

export default ActivitySection;
